import { useState } from "react"
import { Check, Globe } from "lucide-react"
import { useLanguage, SUPPORTED_LANGUAGES, type LangCode } from "@/clientWeb/i18n/LanguageContext"
import { translate } from "@/clientWeb/i18n/translate"
import { toast } from "@/clientWeb/components/ui/toast-utils"

/**
 * A language's name in the language the UI is shown in ("Allemand" for German
 * in a French UI), from the browser's own CLDR data. Falls back to the English
 * name where Intl.DisplayNames is unavailable.
 */
function displayName(code: LangCode, uiLanguage: LangCode, fallback: string): string {
  try {
    return new Intl.DisplayNames([uiLanguage], { type: "language" }).of(code) ?? fallback
  } catch {
    return fallback
  }
}

export default function Languages() {
  const { language, setLanguage, t } = useLanguage()
  const [search] = useState("")
  const [saved, setSaved] = useState(false)

  const filtered = SUPPORTED_LANGUAGES.filter(
    l =>
      l.name.toLowerCase().includes(search.toLowerCase()) ||
      l.nativeName.toLowerCase().includes(search.toLowerCase())
  )

  const current = SUPPORTED_LANGUAGES.find(l => l.code === language)

  const handleSelect = (code: LangCode) => {
    const lang = SUPPORTED_LANGUAGES.find(l => l.code === code)
    setLanguage(code)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
    // `t` still closes over the previous language here, so the confirmation
    // came out in the language just left. Say it in the one just chosen.
    toast.success({ title: translate(code, "languages.languageChanged"), description: lang ? `${lang.flag} ${lang.nativeName}` : undefined })
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Page Header */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white">
          {t("languages.title")}
        </h2>
        <p className="text-muted-foreground text-sm mt-1">
          {t("languages.subtitle")}
        </p>
      </div>

      {/* Current Language Banner */}
      <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-4 border border-primary/20 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-card dark:bg-card flex items-center justify-center shadow-sm text-3xl shrink-0">
            {current?.flag}
          </div>
          <div>
            <p className="text-xs tracking-widest text-slate-400 dark:text-foreground mb-0.5">
              {t("languages.currentLanguage")}
            </p>
            <p lang={current?.code} className="text-xl font-semibold text-slate-900 dark:text-foreground/80 dark:text-white leading-tight">
              {current?.nativeName}
            </p>
            {/* <p className="text-sm text-slate-500 dark:text-slate-400">{current?.nativeName}</p> */}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {saved && (
            <span className="text-xs font-medium text-green-600 dark:text-green-400 animate-in fade-in duration-300">
              ✓ {t("languages.languageChanged")}
            </span>
          )}
          <Globe className="w-10 h-10 text-primary/30 shrink-0" />
        </div>
      </div>
      {/* Language Grid */}
      <div className="bg-card dark:bg-card rounded-2xl shadow-xs border border-stroke-100 dark:border-border dark:border-border p-5">
        <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 tracking-wider mb-4">
          {t("languages.allLanguages")}
        </h3>
        <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map(lang => {
            const active = language === lang.code
            // Each language under its own name, as the sign-in page's picker
            // lists them, with the name in the current UI language beneath —
            // unless that is the same word.
            const localName = displayName(lang.code, language, lang.name)
            return (
              <button
                key={lang.code}
                aria-pressed={active}
                onClick={() => handleSelect(lang.code)}
                className={`flex items-center gap-3 p-3.5 rounded-xl border-1 transition-all duration-200 text-left group ${active
                    ? "border-primary bg-primary/5 dark:bg-primary/10 shadow-sm"
                    : "border-stroke-100 dark:border-border dark:border-border hover:border-primary/40 hover:bg-slate-50 dark:hover:bg-slate-700/50"
                  }`}
              >
                <div className="w-11 h-11 rounded-xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-2xl shrink-0 transition-transform group-hover:scale-105">
                  {lang.flag}
                </div>
                <div className="flex-1 min-w-0">
                  <p lang={lang.code} className={`font-semibold text-sm truncate ${active ? "text-primary" : "text-slate-900 dark:text-foreground/80 dark:text-white"}`}>
                    {lang.nativeName}
                  </p>
                  {localName.toLocaleLowerCase() !== lang.nativeName.toLocaleLowerCase() && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{localName}</p>
                  )}
                </div>
                {active && (
                  <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
              </button>
            )
          })}
        </div>
        {filtered.length === 0 && (
          <p className="text-center text-sm text-slate-400 py-6">{t("common.noData")}</p>
        )}
      </div>
    </div>
  )
}
